import { inject, injectable, optional } from "inversify";
import * as THREE from "three";
import { type IService, Logger } from "@designboticteam/core";
import { CAMERA_CONTROL_SERVICE, type ICameraControlService } from "../CameraControlService";
import { CameraService } from "../CameraService";
import { BASE_DISTANCE, DEFAULT_VIEW, VIEW_PRESETS, ZOOM_IN_FACTOR, ZOOM_OUT_FACTOR } from "./constants";
import type { CameraPreset } from "./types";

@injectable()
class CameraViewService implements IService {
	readonly serviceName = "👁️ CameraViewService";
	private logger = new Logger(`🎬 WORKSPACE 3D | ${this.serviceName}`, {
		color: "#ffffff",
		backgroundColor: "#0066cc",
	});

	#zoom = 1;

	constructor(
		@inject(CameraService) private cameraService: CameraService,
		@inject(CAMERA_CONTROL_SERVICE) @optional() private cameraControlService?: ICameraControlService,
	) {}

	public initialize(): void {
		this.logger.log("initialized");
	}

	public dispose(): void {
		this.logger.log("disposed");
	}

	public zoomIn(): void {
		this.setZoom(this.#zoom * ZOOM_IN_FACTOR);
	}

	public zoomOut(): void {
		this.setZoom(this.#zoom * ZOOM_OUT_FACTOR);
	}

	public resetView(): void {
		this.loadViewPreset(DEFAULT_VIEW);
	}

	public loadViewPreset(presetName: CameraPreset): void {
		this.logger.log(`Loading camera preset: ${presetName}`);

		const { position, target } = VIEW_PRESETS[presetName];
		const targetVec = new THREE.Vector3(target.x, target.y, target.z);
		const camera = this.cameraService.camera;

		this.#zoom = 1;

		camera.position.set(position.x, position.y, position.z);
		camera.lookAt(targetVec);

		this.cameraControlService?.setTargetPoint(targetVec);
		this.cameraControlService?.onCameraUpdated.dispatch({ camera });
	}

	private setZoom(zoom: number): void {
		const target = this.cameraControlService?.targetPoint;

		if (target === undefined) {
			return;
		}

		const camera = this.cameraService.camera;
		const direction = camera.position.clone().sub(target).normalize();
		const distance = BASE_DISTANCE / zoom;
		const position = target.clone().add(direction.multiplyScalar(distance));

		camera.position.copy(position);
		camera.lookAt(target);

		this.cameraControlService?.setTargetPoint(target);
		this.cameraControlService?.onCameraUpdated.dispatch({ camera });

		this.#zoom = zoom;
	}
}

export { CameraViewService };
